// frontend/.../public/vendorPrequal/HighRiskForm.tsx
// Schema-driven renderer for HSE / Quality / IR sections (Parts 4-6).
import { HighRiskField, HighRiskGroup, HighRiskPart, HighRiskSection } from './highRiskSchema';
import FileDropZone from './FileDropZone';
import Button from '../../shared/Button';
import { FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../../shared/FormInput';
import { TEXT } from '../../shared/Typography';

interface Props {
  parts: HighRiskPart[];
  values: Record<string, any>;
  files: Record<string, File[]>;
  onChange: (key: string, value: any) => void;
  onFileChange: (key: string, files: FileList | null, multiple?: boolean) => void;
  onRemoveFile: (key: string, idx: number) => void;
}

function FileRow({ f, onRemove }: { f: File; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-2 mt-1 text-sm">
      <span className="text-gray-700">📎 {f.name}</span>
      <span className="text-gray-500">({Math.round(f.size / 1024)} KB)</span>
      <Button variant="linkDanger" size="xs" onClick={onRemove}>
        remove
      </Button>
    </div>
  );
}

function FieldRenderer({
  field,
  values,
  files,
  onChange,
  onFileChange,
  onRemoveFile,
}: {
  field: HighRiskField;
  values: Record<string, any>;
  files: Record<string, File[]>;
  onChange: (key: string, value: any) => void;
  onFileChange: (key: string, files: FileList | null, multiple?: boolean) => void;
  onRemoveFile: (key: string, idx: number) => void;
}) {
  switch (field.type) {
    case 'yesno': {
      const v = values[field.key];
      const showFollowups = v === true;
      return (
        <div className="mb-4">
          <label className={FIELD_LABEL_CLASS}>{field.label}</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={v === true}
                onChange={() => onChange(field.key, true)}
              />{' '}
              Yes
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={v === false}
                onChange={() => onChange(field.key, false)}
              />{' '}
              No
            </label>
          </div>
          {showFollowups && field.detailsKey && (
            <div className="mt-2">
              <label className={`block mb-1 ${TEXT.bodyMuted}`}>Provide details</label>
              <textarea
                rows={3}
                value={values[field.detailsKey] || ''}
                onChange={e => onChange(field.detailsKey!, e.target.value)}
                className={FIELD_INPUT_CLASS}
              />
            </div>
          )}
          {showFollowups && field.uploadKey && (
            <div className="mt-2">
              <label className={`block mb-1 ${TEXT.bodyMuted}`}>
                Upload files that support the above
              </label>
              <FileDropZone
                fieldKey={field.uploadKey}
                multiple
                onFiles={onFileChange}
              />
              {(files[field.uploadKey] || []).map((f, i) => (
                <FileRow
                  key={i}
                  f={f}
                  onRemove={() => onRemoveFile(field.uploadKey!, i)}
                />
              ))}
            </div>
          )}
        </div>
      );
    }
    case 'textarea': {
      const v = values[field.key] || '';
      const hasValue = String(v).trim().length > 0;
      return (
        <div className="mb-4">
          <label className={FIELD_LABEL_CLASS}>{field.label}</label>
          <textarea
            rows={3}
            value={v}
            onChange={e => onChange(field.key, e.target.value)}
            className={FIELD_INPUT_CLASS}
          />
          {field.uploadKey && hasValue && (
            <div className="mt-2">
              <label className={`block mb-1 ${TEXT.bodyMuted}`}>
                Upload files that support the above
              </label>
              <FileDropZone
                fieldKey={field.uploadKey}
                multiple
                onFiles={onFileChange}
              />
              {(files[field.uploadKey] || []).map((f, i) => (
                <FileRow
                  key={i}
                  f={f}
                  onRemove={() => onRemoveFile(field.uploadKey!, i)}
                />
              ))}
            </div>
          )}
        </div>
      );
    }
    case 'person': {
      return (
        <div className="mb-4 border border-gray-200 rounded-lg p-3">
          <div className={`mb-2 ${TEXT.label}`}>{field.label}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={`block mb-1 ${TEXT.caption}`}>Name</label>
              <input
                type="text"
                value={values[`${field.key}.name`] || ''}
                onChange={e => onChange(`${field.key}.name`, e.target.value)}
                className={FIELD_INPUT_CLASS}
              />
            </div>
            <div>
              <label className={`block mb-1 ${TEXT.caption}`}>Title</label>
              <input
                type="text"
                value={values[`${field.key}.title`] || ''}
                onChange={e => onChange(`${field.key}.title`, e.target.value)}
                className={FIELD_INPUT_CLASS}
              />
            </div>
            <div>
              <label className={`block mb-1 ${TEXT.caption}`}>Experience</label>
              <input
                type="text"
                value={values[`${field.key}.experience`] || ''}
                onChange={e => onChange(`${field.key}.experience`, e.target.value)}
                className={FIELD_INPUT_CLASS}
              />
            </div>
          </div>
        </div>
      );
    }
    case 'upload': {
      return (
        <div className="mb-4">
          <label className={FIELD_LABEL_CLASS}>{field.label}</label>
          <FileDropZone
            fieldKey={field.key}
            multiple={!!field.multiple}
            accept={field.accept}
            onFiles={onFileChange}
          />
          {(files[field.key] || []).map((f, i) => (
            <FileRow
              key={i}
              f={f}
              onRemove={() => onRemoveFile(field.key, i)}
            />
          ))}
        </div>
      );
    }
    default:
      return null;
  }
}

function GroupRenderer(props: {
  group: HighRiskGroup;
  values: Record<string, any>;
  files: Record<string, File[]>;
  onChange: (key: string, value: any) => void;
  onFileChange: (key: string, files: FileList | null, multiple?: boolean) => void;
  onRemoveFile: (key: string, idx: number) => void;
}) {
  const { group } = props;
  return (
    <div className="mb-5">
      {group.title && (
        <h4 className={`mb-3 uppercase tracking-wide ${TEXT.subheading}`}>
          {group.title}
        </h4>
      )}
      {group.fields.map(field => (
        <FieldRenderer key={field.key} field={field} {...props} />
      ))}
    </div>
  );
}

function SectionRenderer(props: {
  section: HighRiskSection;
  values: Record<string, any>;
  files: Record<string, File[]>;
  onChange: (key: string, value: any) => void;
  onFileChange: (key: string, files: FileList | null, multiple?: boolean) => void;
  onRemoveFile: (key: string, idx: number) => void;
}) {
  const { section } = props;
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h3 className="text-lg font-semibold text-blue-900 mb-1 border-b pb-2">{section.title}</h3>
      {section.intro && <div className={`mb-3 ${TEXT.helper}`}>{section.intro}</div>}
      {section.groups.map((group, i) => (
        <GroupRenderer key={i} group={group} {...props} />
      ))}
    </div>
  );
}

export default function HighRiskForm(props: Props) {
  return (
    <div>
      {props.parts.map(part => (
        <div key={part.key} className="mb-8">
          <h2 className="text-xl font-bold text-blue-950 mb-4">{part.title}</h2>
          {part.sections.map(section => (
            <SectionRenderer
              key={section.title}
              section={section}
              values={props.values}
              files={props.files}
              onChange={props.onChange}
              onFileChange={props.onFileChange}
              onRemoveFile={props.onRemoveFile}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
